import {
	type AnchorHTMLAttributes,
	type ReactNode,
	useEffect,
	useState,
} from 'react';

import Button from 'components/Button';
import DecorativeVideo from 'components/DecorativeVideo';
import DiscountBubble, {
	type DiscountBubbleSize,
} from 'components/DiscountBubble';
import IconButton from 'components/IconButton';
import Img from 'components/Img';
import { OptionalLink } from 'components/Link';
import {
	LoadingProductCardSkeletonItem,
	LoadingSkeleton,
} from 'components/LoadingSkeleton';
import Message from 'components/Message';
import Popover from 'components/Popover';
import ProductAnimation from 'components/ProductAnimation';
import ProductCard from 'components/ProductCard';
import {
	useIsEditing,
	usePrefersReducedMotion,
	useValueChangeEffect,
} from 'hooks';
import { useProductList } from 'hooks/data-fetching';
import {
	type GTMItemListId,
	type GTMItemListName,
	useProductListGTMEvents,
} from 'hooks/gtm';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard as ProductCardModel } from 'models/productCard';
import type { ColorString, JSONObject } from 'types';
import { cn, cnm } from 'utils/classNames';
import { filterTruthy } from 'utils/collection';
import { asArray, empty, is, range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

export type BlockMediaProductsDisplayType = 'campaignSpinner' | 'shopByMedia';

export type Variant =
	| 'landscape2x1'
	| 'landscape2x1Half'
	| 'landscape2x1Large'
	| 'landscape2x1Small'
	| 'landscape4x1'
	| 'landscape4x3Small'
	| 'portrait'
	| 'square'
	| 'squareSmall';

interface ImageSize {
	height: number;
	width: number;
}

export const IMAGE_SIZES: Record<Variant, ImageSize> = {
	landscape2x1: { width: 1088, height: 544 },
	landscape2x1Half: { width: 810, height: 405 },
	landscape2x1Small: { width: 532, height: 266 },
	landscape2x1Large: { width: 1644, height: 822 },
	landscape4x1: { width: 1644, height: 411 },
	landscape4x3Small: { width: 720, height: 540 },
	portrait: { width: 532, height: 709 },
	square: { width: 720, height: 720 },
	squareSmall: { width: 360, height: 360 },
} as const;

interface Image {
	alt: string | undefined;
	className?: string;
	src: string | undefined;
	variant: Variant;
}
interface Video extends Omit<DigizuiteAsset, 'formatName'> {
	className?: string;
}

interface BaseProps {
	/** Background color for any base price text */
	basePriceTextBackground?: ColorString;

	/** Added on top of the image before the buttons. */
	beforeButtonsContent?: ReactNode;

	campaignBubbleLabel?: string;
	campaignBubbleLargeSize?: DiscountBubbleSize;
	campaignBubbleSale?: boolean;
	campaignBubbleSmallSize?: DiscountBubbleSize;
	campaignBubbleText?: string;

	className?: string;

	/** Link for the image or video. */
	href?: string;

	image?: Image | Image[];
	/** Make the image high priority and preload it, only use when the image is visible above the fold. */
	imagePriority?: boolean;

	linkTarget?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];

	/** Class name for the image or video. */
	mediaClassName?: string;

	/** Loading attribute for the image or video. */
	mediaLoading?: 'eager' | 'lazy';

	/** Class name for the element wrapping the image or video. */
	mediaWrapperClassName?: string;
	preventAutoplay?: boolean;
	productAnimationPlacementClassName?: string;
	video?: Video | Video[];
}

interface PropsWithoutProducts extends BaseProps {
	gtmItemListId?: never;
	gtmItemListName?: never;
	products?: never;
	productsDisplayType?: never;
	productsRequestBody?: never;
}

// Require either products or request body.
interface ProductsBaseProps extends BaseProps {
	/** GTM list ID for the products. */
	gtmItemListId: GTMItemListId;
	/** GTM list name for the products. */
	gtmItemListName: GTMItemListName;
	products?: ProductCardModel[];
	/** How to display the products. */
	productsDisplayType: BlockMediaProductsDisplayType;
	productsRequestBody?: JSONObject;
}
interface PropsWithProducts extends ProductsBaseProps {
	/** Products to display as a campaign spinner or 'shop by media' popover. */
	products: ProductCardModel[] | undefined;
}
interface PropsWithRequestBody extends ProductsBaseProps {
	/** Request body to use for fetching the products. */
	productsRequestBody: JSONObject | undefined;
}

type Props = PropsWithoutProducts | PropsWithProducts | PropsWithRequestBody;

/**
 * A component that handles video, shopByMedia and campaign for blocks that
 * have it and handles logic for how and when they should be rendered
 */
export default function BlockMedia({
	basePriceTextBackground,
	beforeButtonsContent,
	campaignBubbleLabel,
	campaignBubbleLargeSize,
	campaignBubbleSale,
	campaignBubbleSmallSize,
	campaignBubbleText,
	className,
	gtmItemListId,
	gtmItemListName,
	href,
	image,
	imagePriority = false,
	linkTarget,
	mediaClassName,
	mediaLoading = 'lazy',
	mediaWrapperClassName,
	preventAutoplay,
	productAnimationPlacementClassName,
	products: productsProp = empty.array,
	productsDisplayType,
	productsRequestBody,
	video,
}: Props) {
	const { t, tPlural } = useI18n();
	const isEditing = useIsEditing();
	const [isShopByMediaOpen, setIsShopByMediaOpen] = useState(false);
	const {
		data: lazyProducts,
		error: lazyProductsError,
		isLoading: isLoadingLazyProducts,
	} = useProductList(
		productsRequestBody,
		productsDisplayType === 'shopByMedia' ? isShopByMediaOpen : true,
	);

	// Check reduced motion in an effect since it can't be known on the server.
	const prefersReducedMotion = usePrefersReducedMotion();
	const [isPlaying, setIsPlaying] = useState(!preventAutoplay);
	useEffect(() => {
		if (prefersReducedMotion === true) {
			setIsPlaying(false);
		}
	}, [prefersReducedMotion]);

	const products = productsRequestBody ? lazyProducts?.products : productsProp;
	const hasProducts = is.arrayWithLength(products);

	const images = filterTruthy(asArray(image), 'src');
	const videos = filterTruthy(asArray(video), 'src');

	const hasValidVideo = videos.length > 0;
	// In the editor, a video field without a video asset may get a blank image
	// instead of being empty (e.g. video.src === /blank.png). Since video has
	// priority, this will result in a broken video background showing nothing
	// at all. If the editor doesn't want a video and has only selected an image,
	// that image won't be visible. Only try to show the video in the editor if
	// there is no image selected.
	const hasVideo = isEditing
		? hasValidVideo && images.length === 0
		: hasValidVideo;
	// The video asset has an alt but it's currently not used.
	const hasMediaDescription = Boolean(
		hasVideo ? false : images.some((img) => img.alt),
	);

	const showAnimation =
		productsDisplayType === 'campaignSpinner' && hasProducts && !hasVideo;
	const showShopByMedia =
		productsDisplayType === 'shopByMedia' &&
		(hasProducts || productsRequestBody);

	const { sendSelectItemEvent, sendViewItemListEventOnce } =
		useProductListGTMEvents(
			gtmItemListId ?? 'block',
			gtmItemListName ?? 'Block: Unknown',
		);

	useValueChangeEffect(isLoadingLazyProducts, () => {
		if (!isLoadingLazyProducts && is.arrayWithLength(products)) {
			sendViewItemListEventOnce(products, products.length);
		}
	});

	return (
		<div className={cnm('relative overflow-hidden', className)}>
			<OptionalLink
				href={href}
				target={linkTarget}
				className={cnm(
					'relative block',
					'focus-within:after:focus-outline-overlay',
					mediaWrapperClassName,
				)}
				isScreenReaderHidden={!hasMediaDescription && !campaignBubbleText}
			>
				{hasVideo &&
					videos.map((vid) => (
						<DecorativeVideo
							key={vid.src}
							asset={vid}
							isPlaying={isPlaying}
							loading={mediaLoading}
							className={cnm(vid.className, mediaClassName)}
						/>
					))}
				{!hasVideo &&
					images.map((img) => (
						<Img
							key={img.variant}
							src={img.src}
							alt={img.alt}
							width={IMAGE_SIZES[img.variant].width}
							height={IMAGE_SIZES[img.variant].height}
							className={cnm('w-full', img.className, mediaClassName)}
							priority={imagePriority}
							loading={mediaLoading}
						/>
					))}
				{campaignBubbleText && (
					<>
						<DiscountBubble
							label={campaignBubbleLabel}
							text={campaignBubbleText}
							isPositioned
							isSale={campaignBubbleSale}
							size={campaignBubbleSmallSize}
							className="lg:hidden"
						/>
						<DiscountBubble
							label={campaignBubbleLabel}
							text={campaignBubbleText}
							isPositioned
							isSale={campaignBubbleSale}
							size={campaignBubbleLargeSize}
							className="max-lg:hidden"
						/>
					</>
				)}
			</OptionalLink>
			{beforeButtonsContent}
			{showAnimation && (
				<div
					className={cn(
						'pointer-events-none absolute z-5',
						productAnimationPlacementClassName,
					)}
				>
					<ProductAnimation
						products={products}
						onContainerFocus={() => {
							setIsPlaying(false);
						}}
						onContainerBlur={() => {
							if (!prefersReducedMotion) {
								setIsPlaying(true);
							}
						}}
						onProductClick={sendSelectItemEvent}
						coverHeight
						isPlaying={isPlaying}
						basePriceTextBackground={basePriceTextBackground}
					/>
				</div>
			)}
			{(showAnimation || hasVideo) && (
				<IconButton
					icon={isPlaying ? 'pause' : 'play'}
					text={
						showAnimation
							? isPlaying
								? t('general_pause_product_animation_button_label')
								: t('general_play_product_animation_button_label')
							: isPlaying
								? t('general_pause_video_button_label')
								: t('general_play_video_button_label')
					}
					variant="secondary"
					className={cn(
						'absolute right-4 bottom-4 z-10 focus-visible:focus-outline-inverted',
						// Cover the entire corner it's placed in
						'after:absolute after:-inset-4',
					)}
					onClick={() => {
						setIsPlaying((current) => !current);
					}}
				/>
			)}
			{showShopByMedia && (
				<div className="pointer-events-none absolute inset-0 z-5 flex items-end">
					<Button
						size="sm"
						leadingIcon="cart"
						className={cn(
							'pointer-events-auto mb-4 ml-4 focus-visible:focus-outline-inverted',
							// Cover the corner it's placed in if there's a link, otherwise
							// the entire block.
							!href && 'after:absolute after:inset-0',
							href && 'relative after:absolute after:-inset-4',
						)}
						onClick={() => {
							setIsShopByMediaOpen(true);
						}}
						aria-haspopup="dialog"
					>
						{t('product_blocks_show_products_button')}
					</Button>
					<Popover
						isOpen={isShopByMediaOpen}
						title={t('products_media_popover_heading')}
						onOpen={() => {
							if (is.arrayWithLength(products)) {
								sendViewItemListEventOnce(products, products.length);
							}
						}}
						onClose={() => {
							setIsShopByMediaOpen(false);
						}}
					>
						{isLoadingLazyProducts && (
							<LoadingSkeleton>
								{range(2).map((i) => (
									<LoadingProductCardSkeletonItem
										key={i}
										orientation="horizontal"
										showAddToCartButton
										className={i === 0 ? 'mt-4' : 'mt-8'}
									/>
								))}
							</LoadingSkeleton>
						)}
						{!isLoadingLazyProducts &&
							products?.map((product, i) => (
								<ProductCard
									key={product.id}
									product={product}
									productListIndex={i}
									gtmItemListId={gtmItemListId}
									gtmItemListName={gtmItemListName}
									orientation="horizontal"
									className={i === 0 ? 'mt-4' : 'mt-8'}
									openAddedProductPopover={false}
									showAddToCartButton
								/>
							))}
						{!isLoadingLazyProducts &&
							!hasProducts && (
								// Not the best texts but this should hopefully be rare.
								<>
									{lazyProductsError && (
										<Message
											variant="error"
											text={t('general_critical_error_text')}
										/>
									)}
									{!lazyProductsError && (
										<p>{tPlural('general_amount_of_products', 0)}</p>
									)}
								</>
							)}
					</Popover>
				</div>
			)}
		</div>
	);
}
BlockMedia.displayName = 'BlockMedia';
